import React, { useEffect, useState } from "react";
import emptyInstructionsImage from "../../../../../../images/courses.png";
import {
  EMPTY_INSTRUCTION_MESSAGE,
  EDUCATIONAL_QUALIFICATION_LABEL,
  EDUCATIONAL_QUALIFICATIONS_LABEL,
  EMPTY_EDUCATIONAL_QUALIFICATION_MESSAGE,
  DELETE_EDUCATIONAL_QUALIFICATION_LABEL,
  DELETE_EDUCATIONAL_QUALIFICATION_BODY_LABEL,
} from "../../../../../../constants/Dictionary";
import { EmptyState } from "../../../../../../components/EmptyState/EmptyState";
import { Loader } from "../../../../../../components/core/Loader/Loader";
import LoadingThumbnail from "../../../../../../components/LoadingThumbnail/LoadingThumbnail";
import {
  Button,
  ButtonVariants,
} from "../../../../../../components/core/Button/Button";
import { ReactComponent as AddIcon } from "../../../../../../icons/add.svg";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../components/core/Link/Link";
import { StyledCourses } from "../../../../../Formation/Courses/Courses.styled";
import { HistoryEducationModal } from "./HistoryEducationModal/HistoryEducationModal";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
  CollaboratorHistoryEducation,
} from "../../../../../../types/api/collaborators.type";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../../constants/AppConstants";
import { useAppSelector } from "../../../../../../hooks/store.hook";
import { useDebounce } from "use-debounce";
import { DoubleStructure } from "../../../../../../components/core/DoubleStructure/DoubleStructure";
import { BodyTyp } from "../../../../../../components/Typography/Typography";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../types/AppTypes";
import SearchInput from "../../../../../../components/core/SearchInput/SearchInput";
import { StyledExpandTable } from "../../ProfessionalHistory.styled";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../components/core/Table/Table";
import { PopupConfirm } from "../../../../../../components/PopupConfirm/PopupConfirm";
import { getCollaboratorHistoryEducationsColumns } from "../../../../../../constants/TableColumns";
import { canEditCollaboratorHistory } from "../../../../../../utils/canEditCollaboratorHistory.utils";
import {
  useEducationHistory,
  useEducationHistoryDelete,
} from "../../../../../../hooks/formations.hook";

interface InstructionsHistoryProps {
  setLink: (component: React.ReactNode) => void;
  collaborator: CollaboratorDetails;
  onStatusRefresh: () => void;
  isMyProfile?: boolean;
  currentCmSheet?: CollaboratorCmSheet;
}

export const EducationsHistory = ({
  setLink,
  collaborator,
  onStatusRefresh,
  isMyProfile,
  currentCmSheet,
}: InstructionsHistoryProps) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEducation, setSelectedEducation] =
    useState<CollaboratorHistoryEducation | null>(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { profile } = useAppSelector((state) => state.user);

  const profileId = collaborator.id;

  const { mutateAsync: formationDelete, isPending: pendingFormationDelete } =
    useEducationHistoryDelete(profileId);

  const {
    data: historyEducations,
    isLoading: educationsLoading,
    isFetching: educationsFetching,
  } = useEducationHistory({
    profileId,
    pageNumber,
    searchDebouncedValue,
  });

  const hasData =
    historyEducations !== undefined
      ? historyEducations.pagination.total > 0
      : null;

  const restrictCmActions = currentCmSheet?.careerManagerId === profile.id;

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
    restrictCmActions,
  );

  useEffect(() => {
    if (hasData && canEditHistory) {
      setLink(
        <Link
          onClick={() => setShowAddModal(true)}
          icon={<AddIcon />}
          size={LinkSizeEnum.SMALL}
        >
          {EDUCATIONAL_QUALIFICATION_LABEL}
        </Link>,
      );
    }
    return () => setLink(null);
  }, [hasData, canEditHistory]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  const handleDeleteCourse = async () => {
    try {
      await formationDelete(selectedEducation!.id);
      hideDeletePopup();
      onStatusRefresh();
    } finally {
      setSelectedEducation(null);
      setShowDeleteModal(false);
    }
  };

  const onEdit = (record: CollaboratorHistoryEducation) => {
    setSelectedEducation(record);
    setShowAddModal(true);
  };

  const hideAddModal = () => {
    setSelectedEducation(null);
    setShowAddModal(false);
  };

  const handleSave = () => {
    hideAddModal();
    onStatusRefresh();
  };

  const onDelete = (record: CollaboratorHistoryEducation) => {
    setSelectedEducation(record);
    setShowDeleteModal(true);
  };

  const hideDeletePopup = () => {
    setSelectedEducation(null);
    setShowDeleteModal(false);
  };

  const rowKey = (record: CollaboratorHistoryEducation) => {
    return record.id.toString();
  };

  return (
    <>
      <StyledCourses>
        {educationsLoading && !searchDebouncedValue && hasData === null ? (
          <Loader loading={true}>
            <LoadingThumbnail marginTop={316} />
          </Loader>
        ) : (
          <>
            {!hasData && !searchDebouncedValue ? (
              <div className="empty-state">
                <EmptyState
                  label={EMPTY_INSTRUCTION_MESSAGE}
                  imageSrc={emptyInstructionsImage}
                >
                  {canEditHistory && (
                    <Button
                      label={EDUCATIONAL_QUALIFICATION_LABEL}
                      variant={ButtonVariants.SECONDARY}
                      icon={<AddIcon />}
                      onClick={() => {
                        setShowAddModal(true);
                      }}
                    />
                  )}
                </EmptyState>
              </div>
            ) : (
              <>
                <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
                  <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                    {!educationsFetching &&
                      `${historyEducations?.pagination.total} ${historyEducations?.pagination.total === 1 ? EDUCATIONAL_QUALIFICATION_LABEL : EDUCATIONAL_QUALIFICATIONS_LABEL}`}
                  </BodyTyp>
                  <div className="filters">
                    <SearchInput
                      onChange={setSearchValue}
                      label={EDUCATIONAL_QUALIFICATION_LABEL}
                      name={"search"}
                      value={searchValue}
                    />
                  </div>
                </DoubleStructure>
                <StyledExpandTable>
                  <Table<CollaboratorHistoryEducation>
                    theme={TableThemeEnum.DARK}
                    loading={educationsFetching}
                    rowKey={rowKey}
                    headers={getCollaboratorHistoryEducationsColumns()}
                    scrollY={"calc(100dvh - 502px)"}
                    onEdit={canEditHistory ? onEdit : undefined}
                    onDelete={canEditHistory ? onDelete : undefined}
                    data={historyEducations?.data || []}
                    pagination={{
                      current: pageNumber + 1,
                      onChange: (pageNumber) => setPageNumber(pageNumber - 1),
                      total: historyEducations?.pagination.total ?? 0,
                      pageSize: COLLABORATORS_PAGE_SIZE,
                    }}
                    emptyText={EMPTY_EDUCATIONAL_QUALIFICATION_MESSAGE}
                  />
                </StyledExpandTable>
              </>
            )}
          </>
        )}
      </StyledCourses>
      <PopupConfirm
        isOpen={showDeleteModal}
        label={DELETE_EDUCATIONAL_QUALIFICATION_LABEL}
        popupText={`${DELETE_EDUCATIONAL_QUALIFICATION_BODY_LABEL} ${selectedEducation?.qualification}?`}
        onConfirm={() => {
          handleDeleteCourse();
        }}
        onCancel={() => {
          hideDeletePopup();
        }}
        isLoading={pendingFormationDelete}
      />
      {showAddModal && (
        <HistoryEducationModal
          education={selectedEducation}
          isOpen={showAddModal}
          onClose={() => hideAddModal()}
          onSave={handleSave}
          collaborator={collaborator}
        />
      )}
    </>
  );
};
